import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { getIdToken } from './getIdToken';

const API_HOST = process.env.REACT_APP_API_HOST;
const API_KEY = process.env.REACT_APP_API_KEY || 'API Key';

const getErrorMessage = (statusCode: number) => {
  switch (statusCode) {
    case 401:
      return 'Unauthorized';
    case 422:
      return 'RequestDataValidationError';
    case 500:
      return 'InternalServerError';
    default:
      return 'UnknownError';
  }
};

export const api = axios.create({
  baseURL: API_HOST,
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response == null) {
      return Promise.reject(new Error('レスポンスがありません'));
    }
    const statusCode: number = error.response.status;
    const msg = getErrorMessage(statusCode);
    return Promise.reject(new Error(msg));
  }
);

export const customInstance = async <T>(
  config: AxiosRequestConfig
): Promise<T> => {
  const source = axios.CancelToken.source();
  const idToken = await getIdToken();
  const promise = api({
    ...config,
    headers: {
      'x-api-key': API_KEY,
      Authorization: `Bearer ${idToken}`,
    },
    cancelToken: source.token,
  }).then(({ data }) => data);

  // @ts-ignore
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

/**
 * 汎用APIクライアント
 * 共通のヘッダー設定とエラーハンドリングを提供
 */
export const apiClient = {
  /**
   * GET リクエスト
   */
  async get<T = any>(endpoint: string): Promise<AxiosResponse<T>> {
    const idToken = await getIdToken();
    return axios.get(`${API_HOST}${endpoint}`, {
      headers: {
        'x-api-key': API_KEY,
        'Authorization': `Bearer ${idToken}`
      }
    });
  },

  /**
   * POST リクエスト
   */
  async post<T = any>(endpoint: string, data?: any): Promise<AxiosResponse<T>> {
    const idToken = await getIdToken();
    return axios.post(`${API_HOST}${endpoint}`, data, {
      headers: {
        'x-api-key': API_KEY,
        'Authorization': `Bearer ${idToken}`
      }
    });
  },

  /**
   * PUT リクエスト
   */
  async put<T = any>(endpoint: string, data?: any): Promise<AxiosResponse<T>> {
    const idToken = await getIdToken();
    return axios.put(`${API_HOST}${endpoint}`, data, {
      headers: {
        'x-api-key': API_KEY,
        'Authorization': `Bearer ${idToken}`
      }
    });
  },

  /**
   * DELETE リクエスト
   */
  async delete<T = any>(endpoint: string): Promise<AxiosResponse<T>> {
    const idToken = await getIdToken();
    return axios.delete(`${API_HOST}${endpoint}`, {
      headers: {
        'x-api-key': API_KEY,
        'Authorization': `Bearer ${idToken}`
      }
    });
  }
};
